/**
 * Sentry initialization and PII scrubbing for the React frontend.
 *
 * Must be imported as the FIRST import in main.tsx to ensure Sentry
 * captures all errors from the moment the app boots.
 */

import * as Sentry from '@sentry/react';

// ---------------------------------------------------------------------------
// PII Scrubbing
// ---------------------------------------------------------------------------

/**
 * Regex matching API key patterns that must be redacted before sending to Sentry.
 *
 * Matches:
 *  - sk-ant-xxx (Anthropic keys)
 *  - sk-xxx (OpenAI keys)
 *  - key-xxx (generic key prefixes)
 *  - api_key=xxx / api-key: xxx / apikey=xxx patterns
 */
const API_KEY_REGEX = /(?:sk-|key-|api[_-]?key[=:]\s*)[A-Za-z0-9_-]{20,}/gi;

/** Headers that must always be fully redacted. */
const SENSITIVE_HEADERS = ['authorization', 'x-api-key', 'cookie'];

/**
 * Sentry `beforeSend` hook that strips PII and API keys from error events.
 *
 * Exported so tests can validate scrubbing behavior without triggering a
 * full Sentry init.
 */
export function scrubSensitiveData(
  event: Sentry.ErrorEvent,
  _hint: Sentry.EventHint,
): Sentry.ErrorEvent | null {
  // Scrub top-level message
  if (event.message) {
    event.message = event.message.replace(API_KEY_REGEX, '[REDACTED]');
  }

  // Scrub exception values
  if (event.exception?.values) {
    for (const exception of event.exception.values) {
      if (exception.value) {
        exception.value = exception.value.replace(API_KEY_REGEX, '[REDACTED]');
      }
    }
  }

  // Scrub request headers
  if (event.request?.headers) {
    const headers = event.request.headers as Record<string, string>;
    for (const header of SENSITIVE_HEADERS) {
      if (headers[header]) {
        headers[header] = '[REDACTED]';
      }
    }
  }

  return event;
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/**
 * Initialize Sentry for browser error tracking.
 *
 * Call this once at app startup (in main.tsx) before any other code runs.
 * No-op when `VITE_SENTRY_DSN` is not set (local dev without Sentry).
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.debug('[sentry] VITE_SENTRY_DSN not set — Sentry disabled');
    return;
  }

  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        xhr: true,
      }),
    ],
    tracesSampleRate: 1.0,
    beforeSend: scrubSensitiveData,
  });
}

// Re-export Sentry namespace for use in other files (e.g., ErrorBoundary, reactErrorHandler)
export { Sentry };
